import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { ADMIN_ORGANIZATION_ID, API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { hasOrganizationAccess } from '../utils/auth';
import { fetchDevicePictures } from '../services/apiService';
import { exportTableData } from '../utils/csvExport';
import {
  getAllDates,
  getSuccessPivotData,
  getConnectivityPivotData,
  getRSSIPivotData,
  getBatteryPivotData,
  getTemperaturePivotData,
  getConfigBootReasonPivotData,
  getConfigShadowUpdatesPivotData,
  getConfigImageUploadPivotData,
  getConnectivityHeatmapData,
  getTransmissionWindowHeatmapData
} from '../utils/performanceDataProcessing';

const PerformanceDataContext = createContext();

export const usePerformanceData = () => {
  const context = useContext(PerformanceDataContext);
  if (!context) {
    throw new Error('usePerformanceData must be used within a PerformanceDataProvider');
  }
  return context;
};

export const PerformanceDataProvider = ({ children }) => {
  // Core data states - matching original PerformanceTab exactly
  const [performanceData, setPerformanceData] = useState([]);
  const [configurationData, setConfigurationData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [hourlyConnectivityBins, setHourlyConnectivityBins] = useState([]);
  const [individualDeviceConnectivity, setIndividualDeviceConnectivity] = useState({});
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // UI state - matching original exactly (Line 116-118)
  const [activeTab, setActiveTab] = useState('connectivity');
  const [sortColumn, setSortColumn] = useState('serialNumber');
  const [sortDirection, setSortDirection] = useState('asc');
  const [temperatureUnit, setTemperatureUnit] = useState('celsius');
  
  // Hover/Interaction state - all 4 hover overlays from original
  const [hoverData, setHoverData] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [shadowUpdateHoverData, setShadowUpdateHoverData] = useState(null);
  const [shadowUpdateHoverPosition, setShadowUpdateHoverPosition] = useState({ x: 0, y: 0 });
  const [bootReasonHoverData, setBootReasonHoverData] = useState(null);
  const [bootReasonHoverPosition, setBootReasonHoverPosition] = useState({ x: 0, y: 0 });
  const [payloadHoverData, setPayloadHoverData] = useState(null);
  const [payloadHoverPosition, setPayloadHoverPosition] = useState({ x: 0, y: 0 });
  
  // Image modal state - matching original exactly
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [modalPictures, setModalPictures] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Image cache state for performance optimization
  const [imageCache, setImageCache] = useState({});
  
  // Use ref to prevent multiple concurrent calls
  const loadingRef = useRef(false);


  // Main data loading function
  const loadPerformanceData = useCallback(async (selectedOrgId, startDate, endDate) => {
    console.log('🚀 PerformanceDataProvider loadPerformanceData called with:', { selectedOrgId, startDate, endDate });
    
    if (!selectedOrgId || selectedOrgId === 'undefined' || selectedOrgId === undefined || selectedOrgId === '') {
      console.log('❌ No selectedOrgId or selectedOrgId is invalid, returning early');
      return;
    }

    if (!startDate || !endDate) {
      console.log('❌ No startDate or endDate, returning early');
      return;
    }

    if (loadingRef.current) {
      console.log('⏳ Already loading, skipping duplicate call');
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    setError(null);

    // Create AbortController for request cancellation
    const abortController = new AbortController();

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('🚀 Loading performance data with device-level requests for organization:', selectedOrgId);
      const isAdmin = hasOrganizationAccess(ADMIN_ORGANIZATION_ID);
      console.log('🔐 User has admin access:', isAdmin);
      
      const baseParams = {
        startDate: startDate,
        endDate: endDate
      };

      const requestConfig = {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: abortController.signal
      };

      // Step 1: Get all devices in the organization  
      console.log('📱 Fetching devices for organization:', selectedOrgId);
      const orgDevicesResponse = await axios.get(`${API_BASE_URL}/organizations/${selectedOrgId}/devices`, requestConfig);
      const devices = orgDevicesResponse.data.devices || [];
      
      console.log(`📊 Found ${devices.length} devices, using OPTIMIZED device endpoints...`);
      
      if (devices.length === 0) {
        console.warn('⚠️ No devices found in organization');
        setPerformanceData([]);
        setSummary({ totalDevices: 0, activeDevices: 0, devicesWithData: 0 });
        return;
      }

      // Step 2: Use NEW OPTIMIZED device endpoints for each device
      const deviceOptimizedPromises = devices.map(device => 
        Promise.all([
          // NEW OPTIMIZED endpoints with TimeStream aggregation
          axios.get(`${API_BASE_URL}${API_ENDPOINTS.DEVICES.PERFORMANCE_CONNECTION_COUNT(device.id)}`, { ...requestConfig, params: baseParams }),
          axios.get(`${API_BASE_URL}${API_ENDPOINTS.DEVICES.PERFORMANCE_PAYLOAD_COUNT(device.id)}`, { ...requestConfig, params: baseParams }),
          axios.get(`${API_BASE_URL}${API_ENDPOINTS.DEVICES.PERFORMANCE_CONNECTIVITY_PATTERN(device.id)}`, { ...requestConfig, params: baseParams }),
          axios.get(`${API_BASE_URL}${API_ENDPOINTS.DEVICES.PERFORMANCE_BATTERY_DAILY(device.id)}`, { ...requestConfig, params: baseParams }),
          axios.get(`${API_BASE_URL}${API_ENDPOINTS.DEVICES.PERFORMANCE_TEMPERATURE_DAILY(device.id)}`, { ...requestConfig, params: baseParams }),
          axios.get(`${API_BASE_URL}${API_ENDPOINTS.DEVICES.PERFORMANCE_RSSI_DAILY(device.id)}`, { ...requestConfig, params: baseParams }),
          axios.get(`${API_BASE_URL}${API_ENDPOINTS.DEVICES.PERFORMANCE_BOOT_REASON(device.id)}`, { ...requestConfig, params: baseParams })
        ]).then(results => ({ device, optimizedData: results }))
      );

      // Summary cards now calculated from device data via SharedPerformanceCache

      console.log(`📊 Loading ${deviceOptimizedPromises.length} devices with OPTIMIZED endpoints...`);
      
      // Execute device requests
      const deviceResults = await Promise.allSettled(deviceOptimizedPromises);
      
      // Step 4: Process SIMPLIFIED device data (no complex merging needed)
      const processedDevices = [];
      
      deviceResults.forEach(deviceResult => {
        if (deviceResult.status === 'fulfilled') {
          const { device, optimizedData } = deviceResult.value;
          const [
            connectionCountResult,
            payloadCountResult,
            connectivityPatternResult,
            batteryDailyResult,
            temperatureDailyResult,
            rssiDailyResult,
            bootReasonResult
          ] = optimizedData;

          // DEBUG: Log what we got from each endpoint
          console.log(`🔍 Device ${device.id} endpoint results:`, {
            connectionCount: connectionCountResult.status,
            payloadCount: payloadCountResult.status, 
            batteryDaily: batteryDailyResult.status,
            rssiDaily: rssiDailyResult.status
          });

          if (connectionCountResult.status === 'rejected') {
            console.error(`❌ Connection count failed for device ${device.id}:`, connectionCountResult.reason?.message || connectionCountResult.reason);
          }
          if (batteryDailyResult.status === 'rejected') {
            console.error(`❌ Battery daily failed for device ${device.id}:`, batteryDailyResult.reason?.message || batteryDailyResult.reason);
          }
          if (rssiDailyResult.status === 'rejected') {
            console.error(`❌ RSSI daily failed for device ${device.id}:`, rssiDailyResult.reason?.message || rssiDailyResult.reason);
          }

          // Get actual endpoint data and LOG what we receive
          console.log(`🔍 FULL response structure for device ${device.id}:`, {
            connectionCountFullResponse: connectionCountResult.status === 'fulfilled' ? connectionCountResult.value : 'rejected',
            hasData: connectionCountResult.status === 'fulfilled' && connectionCountResult.value?.data,
            dataKeys: connectionCountResult.status === 'fulfilled' && connectionCountResult.value?.data ? 
              Object.keys(connectionCountResult.value.data) : 'no data'
          });

          const connectionCountData = connectionCountResult.status === 'fulfilled' ? connectionCountResult.value.data : null;
          const batteryDailyData = batteryDailyResult.status === 'fulfilled' ? batteryDailyResult.value.data : null;
          const rssiDailyData = rssiDailyResult.status === 'fulfilled' ? rssiDailyResult.value.data : null;

          console.log(`🔍 RAW endpoint data for device ${device.id}:`, {
            connectionCountData: connectionCountData ? Object.keys(connectionCountData) : 'null',
            connectionResponse: connectionCountResult.value,
            batteryDailyData: batteryDailyData ? Object.keys(batteryDailyData) : 'null', 
            batteryResponse: batteryDailyResult.value,
            dailyConnectivityLength: connectionCountData?.dailyConnectivity?.length || 0,
            dailyBatteryLength: batteryDailyData?.dailyBattery?.length || 0
          });

          // Convert to format expected by existing components
          const simplifiedDevice = {
            deviceId: device.id,
            serialNumber: device.serialnumber,
            meterName: device.externalmeterid || 'N/A',
            deviceName: device.name || 'N/A',
            status: device.isactivated ? 'active' : 'inactive',
            latitude: device.latitude,
            longitude: device.longitude,
            
            // Convert optimized data to old format for component compatibility
            connectivityStats: connectionCountData?.dailyConnectivity?.map(day => ({
              date: day.date,
              connected: day.connected,
              transmissionCount: day.transmissionCount
            })) || [],
            
            transmissionWindowStats: connectionCountData?.dailyConnectivity?.map(day => ({
              date: day.date, 
              connected: day.connected,
              transmissionWindows: day.connectionHours || 1
            })) || [],
            
            batteryStats: batteryDailyData?.dailyBattery ? {
              overallMedian: (batteryDailyData.summary?.overallAverage || 0) * 1000, // Convert V to mV for compatibility
              totalReadings: batteryDailyData.summary?.totalReadings || 0,
              dailyStats: batteryDailyData.dailyBattery.map(day => ({
                date: day.date,
                medianVoltage: day.averageVoltage * 1000, // Convert to mV
                batteryCount: day.readingCount,
                voltages: []
              }))
            } : { overallMedian: 0, totalReadings: 0, dailyStats: [] },
            
            rssiStats: rssiDailyData?.dailyRSSI ? {
              averageRSSI: Math.abs(rssiDailyData.summary?.overallAverage || 0),
              signalQuality: rssiDailyData.summary?.signalQuality || 'none',
              rssiValues: []
            } : { averageRSSI: 0, signalQuality: 'none', rssiValues: [] }
          };

          console.log(`🔍 CONVERTED device data for ${device.id}:`, {
            hasConnectivityStats: simplifiedDevice.connectivityStats.length > 0,
            hasBatteryStats: !!simplifiedDevice.batteryStats && simplifiedDevice.batteryStats.dailyStats.length > 0,
            hasRssiStats: !!simplifiedDevice.rssiStats && simplifiedDevice.rssiStats.averageRSSI > 0
          });

          processedDevices.push(simplifiedDevice);
        }
      });

      // Set SIMPLIFIED performance data
      setPerformanceData(processedDevices);
      
      // Calculate SIMPLIFIED summary from optimized data
      const simplifiedSummary = {
        totalDevices: devices.length,
        activeDevices: devices.filter(d => d.isactivated).length,
        devicesWithData: processedDevices.length,
        averageRSSI: null, // Will calculate from rssiDaily data if needed
        averageSuccessRate: null, // Skip for now (no success rate endpoint yet)
        batteryHealthPercentage: null // Will update from card data
      };
      setSummary(simplifiedSummary);

      // Summary data now comes from SharedPerformanceCache device data calculations
      console.log('📊 Device data loaded, summary will be calculated by SharedPerformanceCache');

      console.log('✅ Parallel performance data loading completed');
    } catch (err) {
      console.error('❌ Error loading performance data:', err);
      // Don't set error state for aborted requests
      if (err.name !== 'AbortError' && !abortController.signal.aborted) {
        setError(err.response?.data?.error || err.message || 'Failed to load performance data');
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false);
        loadingRef.current = false;
      }
    }

    // Cleanup is handled internally via AbortController
  }, []);

  // Image cache management functions
  const updateImageCache = useCallback((deviceId, imageData) => {
    setImageCache(prev => ({
      ...prev,
      [deviceId]: imageData
    }));
  }, []);

  const loadDeviceImages = useCallback(async (deviceIds) => {
    const newImages = {};
    const loadPromises = deviceIds.map(async (deviceId) => {
      if (!imageCache[deviceId]) {
        try {
          const picturesData = await fetchDevicePictures(deviceId);
          const pictures = picturesData.pictures || [];
          if (pictures.length > 0) {
            newImages[deviceId] = pictures;
          }
        } catch (error) {
          console.error(`Error loading images for device ${deviceId}:`, error);
        }
      }
    });

    await Promise.all(loadPromises);
    
    if (Object.keys(newImages).length > 0) {
      setImageCache(prev => ({ ...prev, ...newImages }));
    }
  }, [imageCache]);

  // Tab management
  const handleTabChange = useCallback((newTab) => {
    console.log('🎯 TAB CHANGE:', newTab);
    setActiveTab(newTab);
  }, []);

  // Sorting management
  const handleSort = useCallback((column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn, sortDirection]);

  // Temperature unit management
  const handleTemperatureUnitChange = useCallback((unit) => {
    setTemperatureUnit(unit);
  }, []);

  // Image modal management - matching original functionality exactly
  const handleImageModalOpen = useCallback(async (image, deviceId) => {
    setSelectedImage({ ...image, deviceId });
    setImageModalOpen(true);
    
    // Load all pictures for this device for navigation
    try {
      const picturesData = await fetchDevicePictures(deviceId);
      const pictures = picturesData.pictures || [];
      setModalPictures(pictures);
      
      // Find current image index
      const currentIndex = pictures.findIndex(pic => pic.url === image.url);
      setCurrentImageIndex(currentIndex >= 0 ? currentIndex : 0);
    } catch (error) {
      console.error('Error loading device pictures for modal:', error);
      setModalPictures([image]);
      setCurrentImageIndex(0);
    }
  }, []);

  const handleImageModalClose = useCallback(() => {
    setImageModalOpen(false);
    setSelectedImage(null);
    setModalPictures([]);
    setCurrentImageIndex(0);
  }, []);

  const handleNextImage = useCallback(() => {
    if (currentImageIndex < modalPictures.length - 1) {
      const newIndex = currentImageIndex + 1;
      setCurrentImageIndex(newIndex);
      setSelectedImage({ ...modalPictures[newIndex], deviceId: selectedImage.deviceId });
    }
  }, [currentImageIndex, modalPictures, selectedImage]);

  const handlePrevImage = useCallback(() => {
    if (currentImageIndex > 0) {
      const newIndex = currentImageIndex - 1;
      setCurrentImageIndex(newIndex);
      setSelectedImage({ ...modalPictures[newIndex], deviceId: selectedImage.deviceId });
    }
  }, [currentImageIndex, modalPictures, selectedImage]);

  // Hover data management - all 4 hover types
  const setHover = useCallback((type, data, position) => {
    switch (type) {
      case 'digitPosition':
        setHoverData(data);
        setHoverPosition(position || { x: 0, y: 0 });
        break;
      case 'shadowUpdate':
        setShadowUpdateHoverData(data);
        setShadowUpdateHoverPosition(position || { x: 0, y: 0 });
        break;
      case 'bootReason':
        setBootReasonHoverData(data);
        setBootReasonHoverPosition(position || { x: 0, y: 0 });
        break;
      case 'payload':
        setPayloadHoverData(data);
        setPayloadHoverPosition(position || { x: 0, y: 0 });
        break;
    }
  }, []);

  const clearAllHovers = useCallback(() => {
    setHoverData(null);
    setShadowUpdateHoverData(null);
    setBootReasonHoverData(null);
    setPayloadHoverData(null);
  }, []);

  // Data sorting functions - matching original Lines 1569-1635
  const sortedDevices = React.useMemo(() => {
    if (!performanceData || performanceData.length === 0) return [];
    
    return [...performanceData].sort((a, b) => {
      let aValue, bValue;
      let isStringSort = false;
      
      switch (sortColumn) {
        case 'serialNumber':
          aValue = a.serialNumber || '';
          bValue = b.serialNumber || '';
          isStringSort = true;
          break;
        case 'meterName':
          aValue = a.meterName || '';
          bValue = b.meterName || '';
          isStringSort = true;
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          isStringSort = true;
          break;
        case 'successRate':
          aValue = a.successRate !== null && a.successRate !== undefined ? parseFloat(a.successRate) : -1;
          bValue = b.successRate !== null && b.successRate !== undefined ? parseFloat(b.successRate) : -1;
          isStringSort = false;
          break;
        case 'rssi':
          aValue = a.rssiStats?.averageRSSI !== null && a.rssiStats?.averageRSSI !== undefined ? parseFloat(a.rssiStats.averageRSSI) : -999;
          bValue = b.rssiStats?.averageRSSI !== null && b.rssiStats?.averageRSSI !== undefined ? parseFloat(b.rssiStats.averageRSSI) : -999;
          isStringSort = false;
          break;
        case 'transmitSuccess':
          aValue = a.transmitStats?.successPercentage !== null && a.transmitStats?.successPercentage !== undefined ? parseFloat(a.transmitStats.successPercentage) : -1;
          bValue = b.transmitStats?.successPercentage !== null && b.transmitStats?.successPercentage !== undefined ? parseFloat(b.transmitStats.successPercentage) : -1;
          isStringSort = false;
          break;
        default:
          return 0;
      }
      
      // Handle string vs numeric comparison
      let comparison;
      if (isStringSort) {
        // Ensure we have strings and use localeCompare
        const aStr = String(aValue || '');
        const bStr = String(bValue || '');
        comparison = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
      } else {
        // Numeric comparison
        if (aValue < bValue) comparison = -1;
        else if (aValue > bValue) comparison = 1;
        else comparison = 0;
      }
      
      return sortDirection === 'desc' ? -comparison : comparison;
    });
  }, [performanceData, sortColumn, sortDirection]);
  
  const sortedConfigurationDevices = React.useMemo(() => {
    if (!configurationData || configurationData.length === 0) return [];
    
    return [...configurationData].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortColumn) {
        case 'serialNumber':
          aValue = a.serialNumber || '';
          bValue = b.serialNumber || '';
          break;
        case 'meterName':
          aValue = a.meterName || '';
          bValue = b.meterName || '';
          break;
        default:
          return 0;
      }
      
      // Ensure we have strings for localeCompare
      const aStr = String(aValue || '');
      const bStr = String(bValue || '');
      const comparison = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
      return sortDirection === 'desc' ? -comparison : comparison;
    });
  }, [configurationData, sortColumn, sortDirection]);

  // Export handlers for different performance table types - matching original Lines 657-733
  const handleExportSuccessRate = useCallback(() => {
    const dates = getAllDates(performanceData);
    const pivotData = getSuccessPivotData(performanceData);
    return exportTableData('success-rate-pivot', { pivotData, dates });
  }, [performanceData]);

  const handleExportConnectivity = useCallback(() => {
    const dates = getAllDates(performanceData);
    const pivotData = getConnectivityPivotData(performanceData);
    return exportTableData('connectivity-pivot', { pivotData, dates });
  }, [performanceData]);

  const handleExportRSSI = useCallback(() => {
    const dates = getAllDates(performanceData);
    const pivotData = getRSSIPivotData(performanceData);
    return exportTableData('rssi-pivot', { pivotData, dates });
  }, [performanceData]);

  const handleExportBattery = useCallback(() => {
    const dates = getAllDates(performanceData);
    const pivotData = getBatteryPivotData(performanceData);
    return exportTableData('battery-pivot', { pivotData, dates });
  }, [performanceData]);

  const handleExportTemperature = useCallback(() => {
    const dates = getAllDates(performanceData);
    const pivotData = getTemperaturePivotData(performanceData);
    return exportTableData('temperature-pivot', { pivotData, dates });
  }, [performanceData]);

  const handleExportBootReason = useCallback(() => {
    const dates = getAllDates(configurationData);
    const pivotData = getConfigBootReasonPivotData(configurationData);
    return exportTableData('boot-reason-pivot', { pivotData, dates });
  }, [configurationData]);

  const handleExportShadowUpdates = useCallback(() => {
    const dates = getAllDates(configurationData);
    const pivotData = getConfigShadowUpdatesPivotData(configurationData);
    return exportTableData('shadow-updates-pivot', { pivotData, dates });
  }, [configurationData]);

  const handleExportImageUploads = useCallback(() => {
    const dates = getAllDates(configurationData);
    const pivotData = getConfigImageUploadPivotData(configurationData);
    return exportTableData('image-uploads-pivot', { pivotData, dates });
  }, [configurationData]);

  const handleExportDataPayloads = useCallback(() => {
    const dates = getAllDates(performanceData);
    const pivotData = getConnectivityPivotData(performanceData);
    const heatmapData = [];
    
    // Build heatmap data for export from pivot data
    pivotData.forEach(device => {
      const row = {
        'Device Serial': device.serialNumber,
        'Device Name': device.meterName || 'Unknown'
      };
      
      dates.forEach(date => {
        const dayData = device[date];
        row[date] = dayData ? (dayData.transmissionCount || 0) : 0;
      });
      
      heatmapData.push(row);
    });
    
    return exportTableData('daily-data-payloads-heatmap', { devices: heatmapData, dates });
  }, [performanceData]);

  const handleExportTransmissionEvents = useCallback(() => {
    const dates = getAllDates(performanceData);
    const heatmapData = [];
    
    // Build heatmap data for export from performance data
    performanceData.forEach(device => {
      const row = {
        'Device Serial': device.serialNumber,
        'Device Name': device.meterName || 'Unknown'
      };
      
      dates.forEach(date => {
        const transmissionStat = device.transmissionWindowStats?.find(stat => stat.date === date);
        row[date] = transmissionStat ? (transmissionStat.transmissionWindows || 0) : 0;
      });
      
      heatmapData.push(row);
    });
    
    return exportTableData('daily-transmission-events-heatmap', { devices: heatmapData, dates });
  }, [performanceData]);

  // Clear data function
  const clearPerformanceData = useCallback(() => {
    setPerformanceData([]);
    setConfigurationData([]);
    setSummary(null);
    setHourlyConnectivityBins([]);
    setIndividualDeviceConnectivity({});
    setError(null);
    clearAllHovers();
  }, [clearAllHovers]);

  const contextValue = {
    // Data states - matching original exactly
    performanceData,
    configurationData,
    summary,
    hourlyConnectivityBins,
    individualDeviceConnectivity,
    
    // Sorted data - computed from raw data
    sortedDevices,
    sortedConfigurationDevices,
    
    // Loading and error states
    isLoading,
    error,
    
    // UI states - matching original exactly
    activeTab,
    sortColumn,
    sortDirection,
    temperatureUnit,
    
    // Hover/Interaction state - all 4 hover overlays
    hoverData,
    hoverPosition,
    shadowUpdateHoverData,
    shadowUpdateHoverPosition,
    bootReasonHoverData,
    bootReasonHoverPosition,
    payloadHoverData,
    payloadHoverPosition,
    
    // Image modal state - matching original exactly
    imageModalOpen,
    selectedImage,
    modalPictures,
    currentImageIndex,
    
    // Actions
    loadPerformanceData,
    handleTabChange,
    handleSort,
    handleTemperatureUnitChange,
    handleImageModalOpen,
    handleImageModalClose,
    handleNextImage,
    handlePrevImage,
    setHover,
    clearAllHovers,
    clearPerformanceData,
    
    // Export actions - matching original exactly
    handleExportSuccessRate,
    handleExportConnectivity,
    handleExportRSSI,
    handleExportBattery,
    handleExportTemperature,
    handleExportBootReason,
    handleExportShadowUpdates,
    handleExportImageUploads,
    handleExportDataPayloads,
    handleExportTransmissionEvents
  };

  return (
    <PerformanceDataContext.Provider value={contextValue}>
      {children}
    </PerformanceDataContext.Provider>
  );
};